import React, { useState, useEffect, useContext } from "react";
import { Button } from "antd";
import { CheckSquareOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { AuthContext } from "../context/AuthProvider";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import { device } from "../lib/breakpoints";
import { useConnectStatus } from "../hooks/useConnectStatus";
import { hasLeoKnowledgeBaseContext } from "../helpers/opsChatSetup";

const OnboardingWidget = ({ isVisible, onOpenModal, variant = "fullOs" }) => {
  const { user, userInfo } = useContext(AuthContext);
  const isOpsChat = variant === "opsChat";

  // Check Connect status for real-time payment capability
  const { canAcceptPayments, loading: connectStatusLoading } = useConnectStatus(
    user?.uid
  );

  const [taskStatus, setTaskStatus] = useState(() =>
    isOpsChat
      ? { unit: false, knowledgeBase: false, chatLink: false }
      : { bankAccount: false, property: false, tenant: false }
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpsChat) return;
    if (!user || !isVisible || connectStatusLoading) return;

    const checkTaskStatus = async () => {
      try {
        setIsLoading(true);

        // Check bank account setup - use canAcceptPayments from useConnectStatus
        // This ensures consistency with Settings page and FirstTimeUserModal
        const bankAccountSetup = canAcceptPayments || false;

        // Check if user has properties/units
        const units = await unitModel.getByOwner(user.uid);
        const hasProperty = units && units.length > 0;

        // Check if user has tenants
        const tenantsResult = await listTenants();
        const hasTenant =
          tenantsResult?.tenants && tenantsResult.tenants.length > 0;

        // Only check the three tasks that are actually shown in the modal
        setTaskStatus({
          bankAccount: bankAccountSetup,
          property: hasProperty,
          tenant: hasTenant,
        });
      } catch (error) {
        console.error("Error checking task status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkTaskStatus();

    // Re-check periodically to update status
    const interval = setInterval(checkTaskStatus, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [isOpsChat, user, isVisible, canAcceptPayments, connectStatusLoading]);

  // OpsChat: align with OpsChatFirstTimeModal (first door, knowledge base, share chat link)
  useEffect(() => {
    if (!isOpsChat) return;
    if (!user || !isVisible) return;

    const checkTaskStatus = async () => {
      try {
        setIsLoading(true);

        const units = await unitModel.getByOwner(user.uid);
        const hasKnowledgeBase = hasLeoKnowledgeBaseContext(userInfo, units);
        const hasUnit = units && units.length > 0;
        const hasChatLink = hasUnit;

        setTaskStatus({
          unit: hasUnit,
          knowledgeBase: hasKnowledgeBase,
          chatLink: hasChatLink,
        });
      } catch (error) {
        console.error("Error checking OpsChat task status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkTaskStatus();
    const interval = setInterval(checkTaskStatus, 30000);
    return () => clearInterval(interval);
  }, [isOpsChat, user, isVisible, userInfo]);

  if (!isVisible || isLoading) return null;

  const completedCount = Object.values(taskStatus).filter(Boolean).length;
  const totalTasks = Object.keys(taskStatus).length;
  const allTasksComplete = completedCount === totalTasks;

  // Don't show if all tasks are complete
  if (allTasksComplete) return null;

  const remainingTasks = totalTasks - completedCount;

  return (
    <WidgetContainer>
      <FloatingButton
        type="primary"
        icon={<CheckSquareOutlined />}
        onClick={onOpenModal}
        $hasRemainingTasks={remainingTasks > 0}
      >
        {remainingTasks > 0 && <TaskBadge>{remainingTasks}</TaskBadge>}
      </FloatingButton>
    </WidgetContainer>
  );
};

export default OnboardingWidget;

const WidgetContainer = styled.div`
  position: fixed;
  bottom: calc(2rem + 80px);
  right: 2rem;
  z-index: 10000002 !important;
  pointer-events: auto;
  animation: slideInRight 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  .ant-btn-icon {
    margin: auto;
    margin-inline-end: auto !important;
  }

  @media ${device.mobile} {
    bottom: calc(1rem + 80px);
    right: 1rem;
  }

  @keyframes slideInRight {
    0% {
      opacity: 0;
      transform: translateX(30px) scale(0.8);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
`;

const FloatingButton = styled(Button)`
  width: 64px !important;
  height: 64px !important;
  min-width: 64px !important;
  min-height: 64px !important;
  border-radius: 50% !important;
  background: linear-gradient(135deg, #4bc069 0%, #3ba855 100%) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 8px 32px rgba(75, 192, 105, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative;
  overflow: visible;
  padding: 0 !important;
  line-height: 1 !important;

  .ant-btn-icon {
    margin-inline-end: auto;
    margin: auto;
  }

  /* Pulsing animation to draw attention */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #4bc069;
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1);
    animation: pulse 2s ease-in-out infinite;
    z-index: -1;
    pointer-events: none;
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 40px rgba(75, 192, 105, 0.4);
    background: linear-gradient(135deg, #3ba855 0%, #2c8f4d 100%) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }

  &:active {
    transform: scale(0.95);
    transition: transform 0.1s ease;
  }

  .anticon {
    font-size: 28px !important;
    color: white !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .anticon svg {
    color: white !important;
    fill: white !important;
    width: 28px !important;
    height: 28px !important;
    margin: 0 auto !important;
    display: block !important;
  }

  @media ${device.mobile} {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;

    .anticon {
      font-size: 24px !important;
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.3;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.1;
      transform: translate(-50%, -50%) scale(1.3);
    }
  }
`;

const TaskBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff4d4f;
  border: 2px solid white;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;
